"use client"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import IconMinus from "@/app/(site)/_components/Atoms/Icons/IconMinus"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonText from "@/app/(site)/_components/Molecules/ButtonText"
import { useClickOutside } from "@/app/(site)/_utils/_hooks/useOutsideIdentifier"
import useScrollLock from "@/app/(site)/_utils/_hooks/useScrollLock"
import { getLangCode } from "@/app/(site)/_utils/misc"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { SearchFilter } from "@/lib/depict-client/types/api/ProductListingRequestV3"
import { t } from "@/translations"
import React, { useEffect, useRef, useState } from "react"
import { FilterState } from "../filterReducer"
import FilterOptions from "./FilterOptions"
import { Sort } from "./Types/Sort"

type DesktopFilterProps = {
    clearAllFilters: () => void
    clearFilter: (field: string, value: string) => void
    showFilter?: boolean
    filters: SearchFilter[] // todo fix type
    nHits: number
    params: { locale: string; category: string; subCategory: string[] }
    selectedFiltersOptions: FilterState
    setFilter: (
        field: string,
        op: string,
        value: string | string[] | number | number[],
        e: React.MouseEvent<HTMLDivElement>
    ) => void
    setSort: (sort: string) => void
    clearSort: () => void
}

// Main Product Grid Filter Component
const DesktopFilter = ({
    clearAllFilters,
    clearFilter,
    showFilter,
    filters,
    nHits,
    params,
    selectedFiltersOptions,
    setFilter,
    setSort,
    clearSort
}: DesktopFilterProps) => {
    const [selectedFilter, setSelectedFilter] = useState("")
    const [isAnimating, setIsAnimating] = useState(false)
    const [toggleSort, setToggleSort] = useState(false)

    const filterRef = useRef<HTMLDivElement>(null)
    const backdropRef = useRef<HTMLDivElement>(null)

    const toggleFilterModal = useEcommerceStore(state => state.toggleFilterModal)
    const { preventScroll } = useScrollLock()
    const langCode = getLangCode(params.locale) ?? ""

    const handleToggleFilterModal = () => {
        toggleFilterModal(true)
        preventScroll()
    }

    const closeFilter = () => {
        setIsAnimating(false)
        setToggleSort(false)
        setTimeout(() => {
            setSelectedFilter("")
        }, 500)
    }

    const handleOpenFilter = (filterType: string) => () => {
        if (selectedFilter === filterType) {
            closeFilter()
        } else {
            setSelectedFilter(filterType)
        }
    }

    useClickOutside({
        refs: [filterRef, backdropRef],
        onOutsideClick: closeFilter
    })

    useEffect(() => {
        const shouldAnimate = !!selectedFilter || !!toggleSort
        setIsAnimating(shouldAnimate)
    }, [selectedFilter, toggleSort])

    const moduleClasses = isAnimating ? "translate-y-0" : " -translate-y-[300px]"
    const blurClasses = isAnimating ? "opacity-100" : "opacity-0"
    const selectedFilterData = filters?.find(
        ({ field }: { field: string }) => field === selectedFilter
    )

    const totalSelectedFilters = selectedFiltersOptions.filters.reduce(
        (acc: number, option: { data: string[] }) =>
            acc + (option.data ? option.data.length : 0),
        0
    )

    return (
        <section className="col-span-12 bg-white pb-16 md:pb-24" ref={filterRef}>
            <div className="relative z-20 bg-white">
                <div className="no-scrollbar mb-24 flex gap-8 overflow-x-auto whitespace-nowrap">
                    {selectedFiltersOptions.filters.map(item =>
                        item.data.map((dataItem, index) => {
                            const [label]: string[] = dataItem.split(";")
                            return (
                                <ButtonText
                                    asChild
                                    size="small"
                                    key={`${label}-${index}`}
                                    className="flex items-center gap-2 rounded-full bg-main-medium-grey px-16 py-9"
                                    onClick={() => clearFilter(item.field, dataItem)}
                                    role="button" // Screen readers will interpret it as a button
                                    aria-label={`Close ${dataItem} filter`}
                                >
                                    {label}
                                    <IconClose className="h-15 w-18 cursor-pointer" />
                                </ButtonText>
                            )
                        })
                    )}
                    {totalSelectedFilters > 1 && (
                        <div
                            className="flex cursor-pointer items-center gap-2 rounded-full border border-main-medium-grey bg-main-white px-16 py-9"
                            onClick={clearAllFilters}
                        >
                            <Text size="small" weight="light">
                                {t(langCode, "category_page.clear_all")}
                            </Text>
                        </div>
                    )}
                </div>

                {/* Filters Row */}
                {showFilter && (
                    <div className="relative z-20 grid grid-cols-3 items-center gap-4">
                        <div className="hidden gap-16 md:flex">
                            {filters.map(({ field, meta, op, id }) => {
                                const isActive = selectedFilter === field

                                const filterId = field?.toLowerCase()
                                const activeFilter = selectedFiltersOptions.filters.find(
                                    option => option.field === field
                                )
                                const activeCount = activeFilter
                                    ? activeFilter.data.length
                                    : 0
                                return (
                                    <ButtonText
                                        asChild
                                        key={`${id}-${field}`}
                                        size="big"
                                        iconPosition="right"
                                        icon={
                                            isActive ? (
                                                <IconMinus className="h-18 w-18" />
                                            ) : (
                                                <IconPlus className="h-18 w-18" />
                                            )
                                        }
                                        weight="regular"
                                        tabIndex={0} // Makes the div focusable
                                        onKeyDown={(e: { key: string }) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                handleOpenFilter(filterId) // Open filter on key press
                                            }
                                        }}
                                        role="button" // Screen readers will interpret it as a button
                                        aria-label={`Open ${meta?.group_title} filter`}
                                        className={`flex items-center overflow-visible rounded-md px-4 py-2 ${isActive ? "text-black" : "text-gray-600"} transition-all focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400`}
                                        onClick={handleOpenFilter(filterId)}
                                    >
                                        {meta?.group_title}
                                        {activeCount > 0 && ` (${activeCount})`}
                                    </ButtonText>
                                )
                            })}
                        </div>
                        <div className="flex w-180 md:hidden">
                            <ButtonText
                                size="big"
                                icon={<IconPlus className="h-18 w-18" />}
                                iconPosition="right"
                                onClick={() => handleToggleFilterModal()}
                            >
                                {t(langCode, "category_page.filter")}
                            </ButtonText>
                        </div>
                        <div className="flex justify-center">
                            <Text
                                size="medium"
                                weight="regular"
                                className="text-main-dark-grey"
                            >
                                {nHits} {t(langCode, "cart.items")}
                            </Text>
                        </div>
                        <Sort
                            setSort={setSort}
                            langCode={langCode}
                            setToggleSort={setToggleSort}
                            toggleSort={toggleSort}
                            clearSort={clearSort}
                            selectedSort={selectedFiltersOptions.sort || undefined}
                        />
                    </div>
                )}
            </div>

            {/* Curtain Effect */}
            {(selectedFilter || toggleSort) && (
                <div
                    onClick={closeFilter} // Closes filter on backdrop click
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                            closeFilter()
                        }
                    }}
                    role="button"
                    aria-label="Close filter overlay"
                    className={`absolute left-0 top-auto z-10 h-full w-full bg-black/20 backdrop-blur-md transition-all duration-300 ease-in-out ${blurClasses}`}
                />
            )}
            {selectedFilter && (
                <div
                    className={`absolute left-0 top-auto z-10 grid w-full grid-flow-col grid-cols-4 grid-rows-3 gap-8 bg-white p-24 transition-transform duration-500 ease-in-out ${moduleClasses}`}
                >
                    <FilterOptions
                        filter={selectedFilterData}
                        setFilter={setFilter}
                        selectedFilters={selectedFiltersOptions?.filters || []}
                    />
                </div>
            )}
        </section>
    )
}

export default DesktopFilter
